import React, { PureComponent } from 'react';
import { Flex } from '../../common/Flex';
import { ModalPanelProps } from '../DashboardModal';
import { ExtensiblePanelProps, ExtensiblePanelState } from 'app/common/panel/ExtensiblePanel';
import AppContext from '../../../common/context/AppContext';
import {
    CountType,
    FlightDto,
    FlightLevelBand,
    FlightModelType,
    MapTrafficVolume,
    MapTrafficVolumeListRequest,
    TimePeriod,
} from 'app/model/CORE';
import { ServiceFactory } from 'app/service/ServiceFactory';
import { ConvectionSource, NetworkMapConvection } from 'app/model/SOL49';
import MapUtils from 'app/components/map/util/MapUtils';
import { DEFAULT_FL_FILTER_RANGE } from 'app/components/dashboard/network/AbstractMapView';
import { FlightAndModelType, FlightMapOptions, ProfileSegmentHighlight } from 'app/components/map/model/MapModel';
import Utils from 'app/common/util/Utils';
import VerticalProfileSvg from 'app/components/map/vpd/VerticalProfileSvg';
import { Select, Switch } from 'antd';
import SplitPanel from 'app/common/panel/SplitPanel';
import {
    MapLayer,
    MapLayerType,
    MapLayerUtils,
    MapObjectType,
    SolutionMeasureImpact,
    SolutionTvImpact,
    TvImpactLayerUtils,
    WeatherLayerUtils,
    WeatherMinLevel,
} from 'app/components/dashboard/network/map/Map';
import FlightLevelUtils from 'app/common/util/FlightLevelUtils';
import { LayersIcon } from 'app/components/icons/LayersIcon';
import { MapLayersMenuPopup } from 'app/components/dashboard/network/map/MapLayersMenuPopup';
import MapPanelMapView from 'app/components/panels/map/MapPanelMapView';
import { LayerUpdateEvent } from 'app/common/eventbus/Events';
import { MapSettings } from 'app/components/dashboard/network/map/MapSettings';
import TimePeriodUtils from 'app/common/util/TimePeriodUtils';
import memoizeOne from 'memoize-one';
import ReactDOM from 'react-dom';
import ConditionalRender from 'app/common/components/ConditionalRender';
import { UnityApplication } from 'app/unity/UnityApplication';
import { NMUI4DContext, NMUI4DContextProps } from 'app/common/context/NMUI4DContext';

export interface MapPanelLayerOptions {
    measuresLayerEnabled: boolean;
    weatherLayerEnabled: boolean;
    tvImpactLayerEnabled: boolean;
    showNiaImpactedExtra: boolean;
}
interface MapPanelProps extends ModalPanelProps, ExtensiblePanelProps {
    flights?: Array<FlightDto>;
    tvIds?: Array<string>;
    mapConvection?: NetworkMapConvection;
    mapOptions?: FlightMapOptions;
    showVpd?: boolean;
    impactedTvs?: Array<SolutionTvImpact>;
    fullscreenLike?: boolean;
    impactedMeasures?: Array<SolutionMeasureImpact>;
    layerOptions?: MapPanelLayerOptions;
    timePeriod?: TimePeriod;
}

interface MapPanelState extends ExtensiblePanelState {
    mapTVs: Array<MapTrafficVolume>;
    showOri: boolean;

    //map filtering
    flightLevelFilter: FlightLevelBand;
    mapTime: Date;

    //layers
    layers: Array<MapLayer>;
    layersMenuVisible: boolean;
    layersMenuPosition: number[];

    mapId?: number;

    //helper
    fontSize?: number;

    //vpd
    selectedVpdFlight?: string;
}

export const DEFAULT_MAP_OPTIONS: FlightMapOptions = {
    autoZoom: false,
    listVectorPoints: true,
    showActiveAirspaceOnly: true,
    showActiveDynamicVolumeInVpd: false,
    showActiveDynamicVolumeOnly: false,
    showElementaryAirspaceOnly: true,
    showIfpzPartOfTrajectoryOnly: false,
};

export const DEFAULT_MAP_SETTINGS: MapSettings = {
    convectionRiskLevel: 1,
    showConvectionTvs: false,
    showConvection: true,
    showConvectionLegend: false,
    showConvectionLabel: false,
    hideConvectionWithNoImpact: false,
};

export class MapPanel extends PureComponent<MapPanelProps, MapPanelState> {
    public static INITIAL_WIDTH = '75em';
    public static INITIAL_HEIGHT = '75em';
    private mapPanelMapViewRef = React.createRef<MapPanelMapView>();

    private readonly MAP_WEATHER_LAYER_ID = 3;
    private readonly MAP_MEASURES_LAYER_ID = 2;
    private readonly MAP_IMPACTED_TVS_LAYER_ID = 1;
    private mainComponentRef = React.createRef<Flex>();

    public static contextType = NMUI4DContext;
    private nmui4dContext?: NMUI4DContextProps;

    constructor(props) {
        super(props);
        this.state = Object.assign({}, this.state, {
            mapTVs: [],
            showOri: false,
            layersMenuPosition: [],
            layers: [],
            mapTime: Utils.isNotExists(this.props.timePeriod)
                ? AppContext.getCurrentTime()
                : TimePeriodUtils.contains(this.props.timePeriod, AppContext.getCurrentTime())
                  ? AppContext.getCurrentTime()
                  : this.props.timePeriod.from,
            flightLevelFilter: { min: FlightLevelUtils.ON_GROUND_LEVEL, max: FlightLevelUtils.UNLIMITED_LEVEL },
        });
    }

    initMapLayers() {
        const weatherLayer: MapLayer = {
            objectsToDisplay: MapLayerUtils.toObjectToDisplayArray(
                MapUtils.getConvectionMap(
                    this.props.mapConvection?.networkForecast,
                    this.props.mapConvection?.airspaceDefinition,
                    this.state.mapTime,
                ),
                MapObjectType.CONVECTION,
            ),
            selection: [],
            label: 'Weather',
            mapType: [MapObjectType.CONVECTION],
            priority: 1,
            showLabel: false,
            active: false,
            type: MapLayerType.WEATHER,
            id: this.MAP_WEATHER_LAYER_ID,
            group: WeatherLayerUtils.GROUP_LAYER,
            customProperties: Object.assign(
                {
                    [WeatherLayerUtils.CUSTOM_PROPERTY_LEGEND]: false,
                    [WeatherLayerUtils.CUSTOM_PROPERTY_MIN_LEVEL]: new WeatherMinLevel(1),
                },
                ConvectionSource.METSAFE !== AppContext.getConvectionSource()
                    ? { [WeatherLayerUtils.CUSTOM_PROPERTY_SHOW_TVS]: false }
                    : {},
            ),
        };
        const impactedTvLayer: MapLayer = {
            id: this.MAP_IMPACTED_TVS_LAYER_ID,
            objectsToDisplay: MapLayerUtils.toObjectToDisplayArray(this.props.impactedTvs, MapObjectType.IMPACTED_TV),
            label: 'Impacted Tvs',
            mapType: [MapObjectType.IMPACTED_TV],
            showLabel: true,
            type: MapLayerType.TV, //not relevant
            priority: 1,
            selection: [],
            active: true,
            filters: [
                { active: false, id: 1, name: 'Selection', filterKeys: TvImpactLayerUtils.isSelectionLoadFilter },
                { active: false, id: 2, name: 'Impacted', filterKeys: TvImpactLayerUtils.isNiaImpactedLoadFilter },
            ],
            customProperties: Object.assign({
                [TvImpactLayerUtils.CUSTOM_PROPERTY_SHOW_BEFORE]: false,
                [TvImpactLayerUtils.CUSTOM_PROPERTY_COUNT_TYPE]: CountType.ENTRY,
            }),
        };
        const measuresLayer: MapLayer = {
            objectsToDisplay: MapLayerUtils.toObjectToDisplayArray(
                this.props.impactedMeasures,
                MapObjectType.IMPACTED_MEASURE,
            ),
            selection: [],
            label: 'Measure',
            mapType: [MapObjectType.IMPACTED_MEASURE],
            priority: 5,
            showLabel: false,
            active: true,
            type: MapLayerType.MEASURE,
            id: this.MAP_MEASURES_LAYER_ID,
        };
        const layersEnabled = [];
        if (this.props.layerOptions?.weatherLayerEnabled) {
            layersEnabled.push(weatherLayer);
        }
        if (this.props.layerOptions?.tvImpactLayerEnabled) {
            if (this.props?.layerOptions.showNiaImpactedExtra) {
                impactedTvLayer.filters.push({
                    active: false,
                    id: 5,
                    name: 'Impacted Extra',
                    filterKeys: TvImpactLayerUtils.isNiaImpactedExtraLoadFilter,
                });
            }
            layersEnabled.push(impactedTvLayer);
        }
        if (this.props.layerOptions?.measuresLayerEnabled) {
            layersEnabled.push(measuresLayer);
        }
        this.setState({
            layers: layersEnabled,
        });
    }

    initBusEventListeners() {
        AppContext.eventBus.register(LayerUpdateEvent.NAME, (event: LayerUpdateEvent) => {
            if (event.mapId === this.state.mapId) {
                this.setState(prevState => {
                    return {
                        layers: prevState.layers.map(layer => {
                            if (layer.id === event.layer.id) {
                                return event.layer;
                            }
                            return layer;
                        }),
                    };
                });
            }
        });
    }

    componentDidMount(): void {
        this.nmui4dContext = this.context as NMUI4DContextProps;

        this.refreshMapTrafficVolumeData(this.props.tvIds);
        if (this.props.fullscreenLike) {
            //layers only when in fullscreen mode like
            this.initMapLayers();
        }
        this.initBusEventListeners();

        this.setState({
            fontSize: this.getParentFontSize(),
        });
    }

    componentDidUpdate(prevProps: Readonly<MapPanelProps>, prevState: Readonly<MapPanelState>) {
        if (this.props !== prevProps || this.props.tvIds !== prevProps?.tvIds) {
            this.refreshMapTrafficVolumeData(this.props.tvIds);
        }
        //if flights change we need to re display the impacted tvs as well because the core map gets un mounted depending on if there is flights or not (different layout)
        // if(Utils.isNotShallowEquals(prevProps?.impactedTvs, this.props.impactedTvs) || Utils.isNotShallowEquals(prevProps?.flights, this.props.flights)) {

        this.refreshMapLayers(prevProps, prevState);

        const fontSize = this.getParentFontSize();
        if (prevState.fontSize !== fontSize) {
            this.setState({ fontSize: fontSize });
        }

        if (this.props.flights?.length > 0) {
            //if the selection is empty of the flight list have been changed select the first in the list
            if (
                Utils.isNotExists(this.state.selectedVpdFlight) ||
                Utils.isNotShallowEquals(prevProps?.flights, this.props.flights)
            ) {
                this.setState({
                    selectedVpdFlight: this.props.flights[0].flightId?.flightKeys?.callsign,
                });
            }
        }
    }

    private getParentFontSize() {
        // get fontSize to convert EM to PX (popup width in EM)
        let fontSize = 11.56;
        if (this.mapPanelMapViewRef.current) {
            const fontSizeString = window
                .getComputedStyle(ReactDOM.findDOMNode(this.mapPanelMapViewRef.current)?.parentElement, null)
                .getPropertyValue('font-size');
            if (fontSizeString) {
                fontSize = +fontSizeString.replace('px', '');
            }
        }
        return fontSize;
    }

    public forceMapRender() {
        // this.coreMapRef.current?.forceMapRender();
        this.mapPanelMapViewRef.current?.forceMapRender();
    }

    private refreshMapLayers(prevProps: Readonly<MapPanelProps>, prevState: Readonly<MapPanelState>) {
        if (
            this.props.layerOptions?.weatherLayerEnabled &&
            Utils.isNotShallowEquals(prevProps.mapConvection, this.props.mapConvection)
        ) {
            //update weather layer
            this.setState(prevState => ({
                layers: prevState.layers.map(layer => {
                    if (layer.id === this.MAP_WEATHER_LAYER_ID) {
                        return Object.assign({}, layer, {
                            objectsToDisplay: MapLayerUtils.toObjectToDisplayArray(
                                MapUtils.getConvectionMap(
                                    this.props.mapConvection?.networkForecast,
                                    this.props.mapConvection?.airspaceDefinition,
                                    this.state.mapTime,
                                ),
                                MapObjectType.CONVECTION,
                            ),
                        });
                    }
                    return layer;
                }),
            }));
        }
        if (
            this.props.layerOptions?.tvImpactLayerEnabled &&
            Utils.isNotShallowEquals(prevProps?.impactedTvs, this.props.impactedTvs)
        ) {
            this.setState(prevState => ({
                layers: prevState.layers.map(layer => {
                    if (layer.id === this.MAP_IMPACTED_TVS_LAYER_ID) {
                        return Object.assign({}, layer, {
                            objectsToDisplay: MapLayerUtils.toObjectToDisplayArray(
                                this.props.impactedTvs,
                                MapObjectType.IMPACTED_TV,
                            ),
                        });
                    }
                    return layer;
                }),
            }));
        }

        if (
            this.props.layerOptions?.measuresLayerEnabled &&
            Utils.isNotShallowEquals(prevProps?.impactedMeasures, this.props.impactedMeasures)
        ) {
            this.setState(prevState => ({
                layers: prevState.layers.map(layer => {
                    if (layer.id === this.MAP_MEASURES_LAYER_ID) {
                        return Object.assign({}, layer, {
                            objectsToDisplay: MapLayerUtils.toObjectToDisplayArray(
                                this.props.impactedMeasures,
                                MapObjectType.IMPACTED_TV,
                            ),
                        });
                    }
                    return layer;
                }),
            }));
        }
    }

    refreshMapTrafficVolumeData = (tvIds: Array<string>) => {
        if (tvIds?.length) {
            //TODO use cache
            const request: MapTrafficVolumeListRequest = {
                tvIdList: tvIds,
                dataset: AppContext.getDataset(),
            };
            ServiceFactory.coreService()
                .getMapTrafficVolumeList(request)
                .then(result => {
                    if (!result.error) {
                        this.setState({
                            mapTVs: result.data,
                        });
                    }
                });
        } else {
            this.setState({ mapTVs: [] });
        }
    };

    /**
     * Colour differently the initial trajectory and the rerouted one
     * @param flight
     * @param modelType
     */
    flightTrajectoryColour = (flight: FlightDto, modelType: FlightModelType) => {
        switch (modelType) {
            case FlightModelType.RTFM:
                return '#60B1CC';
            case FlightModelType.FTFM:
                return '#D9D9D9';
        }
    };

    renderMapLayersButtonOnHeader() {
        return (
            <Flex direction="horizontal" style={{ justifyContent: 'flex-end', gap: '1em' }}>
                <Switch
                    checked={this.getShowBeforeFromLayer(this.state.layers)}
                    onChange={this.handleShowBefore}
                    checkedChildren={'Before'}
                    unCheckedChildren={'After'}
                />
                <LayersIcon className="icon" title={'LAYERS'} onClick={this.handleMapLayersMenuVisibleChange} />
                <MapLayersMenuPopup
                    layers={this.state.layers}
                    timePeriod={{
                        from: this.state.mapTime,
                        to: this.state.mapTime,
                    }}
                    flightLevelBand={this.state.flightLevelFilter}
                    visible={this.state.layersMenuVisible}
                    left={this.state.layersMenuPosition[0] - 25 * this.state.fontSize}
                    top={this.state.layersMenuPosition[1]}
                    onClose={() =>
                        this.setState({
                            layersMenuVisible: false,
                        })
                    }
                    mapId={this.state.mapId}
                />
            </Flex>
        );
    }

    handleMapLayersMenuVisibleChange = event => {
        this.setState(prevState => ({
            layersMenuVisible: !prevState.layersMenuVisible,
            layersMenuPosition: [event.clientX, event.clientY],
        }));
    };

    handleShowBefore = (value: boolean) => {
        this.setState(prevState => ({
            layers: prevState.layers.map(layer => {
                if (layer.id === this.MAP_IMPACTED_TVS_LAYER_ID) {
                    return Object.assign({}, layer, {
                        customProperties: Object.assign({}, layer.customProperties, {
                            [TvImpactLayerUtils.CUSTOM_PROPERTY_SHOW_BEFORE]: value,
                        }),
                    });
                }
                return layer;
            }),
        }));
    };

    getShowBeforeFromLayer = memoizeOne((layers: Array<MapLayer>): boolean => {
        const tvImpactLayer: MapLayer = layers.find(layer => layer.id === this.MAP_IMPACTED_TVS_LAYER_ID);
        if (tvImpactLayer)
            return tvImpactLayer.customProperties[TvImpactLayerUtils.CUSTOM_PROPERTY_SHOW_BEFORE] as boolean;
        return false;
    });

    render() {
        const currentTime = AppContext.getCurrentTime();
        const flightData = {
            flights: this.props.flights || [],
            airspaces: [],
            tvMapLocations: [],
        };
        const selectedTVs = this.state.mapTVs;
        let coreMap;
        if (this.props.fullscreenLike) {
            coreMap = (
                <MapPanelMapView
                    ref={this.mapPanelMapViewRef}
                    currentTime={this.state.mapTime}
                    timePeriod={
                        Utils.isExists(this.props.timePeriod) ? this.props.timePeriod : AppContext.getMonitoringPeriod()
                    }
                    selectedTvs={null}
                    selectedFlights={null}
                    selectedFlight={this.getFlightAndModelByFlightId(this.props.flights, this.state.selectedVpdFlight)} //in sync with the vpd selection
                    monitoredTvs={null}
                    visible={true}
                    convectionData={null} //should be displayed in a layer
                    mapSettings={DEFAULT_MAP_SETTINGS}
                    onMapSettingsChanged={null}
                    flightData={flightData}
                    selectedMapTVs={null}
                    selectedMapMeasuresTVs={null}
                    highlightedSegments={undefined}
                    flightLevelFilter={this.state.flightLevelFilter}
                    onFlightLevelFilterChange={this.handleMapFlightLevelBandChange}
                    showTimeReset={true}
                    showTimePeriodReset={false}
                    mapRefTime={this.state.mapTime}
                    onMapRefTimeChange={this.handleMapTimeChange}
                    mapRefTimePeriod={AppContext.getMonitoringPeriod()}
                    timePeriodSelection={false}
                    showTimePeriodSelection={false}
                    onTimePeriodSelectionChange={null} //no time period mode
                    onMapRefTimePeriodChange={null} //no time period mode
                    onResetMapRefTime={this.handleMapResetMapRefTime}
                    onResetMapRefTimePeriod={null}
                    showMapSettings={false} //no map settings button
                    flightLevelFilterLocation="left"
                    headerExtraContent={this.renderMapLayersButtonOnHeader()}
                    headerSize={'1vw'}
                    onMapIdGenerated={this.handleOnCoreMapIdGeneration}
                />
            );
        } else {
            // NOTE(bjarne): In mapless mode the UnityApplication is already created in App.tsx.
            coreMap = (
                <Flex className="map-panel-content-id">
                    {!this.nmui4dContext?.isMapless && <UnityApplication></UnityApplication>}
                </Flex>
            );
        }
        if (this.props.showVpd && this.props.flights?.length) {
            return (
                <Flex direction="vertical" style={{ height: MapPanel.INITIAL_HEIGHT }} ref={this.mainComponentRef}>
                    <SplitPanel
                        orientation="horizontal"
                        splitSize={'75%'}
                        componentA={coreMap}
                        componentB={this.renderRrtVpd(this.props.flights, this.props.mapOptions)}
                        onResize={this.handleOnSplitSize}
                    />
                </Flex>
            );
        } else {
            return (
                <Flex direction="vertical" style={{ height: MapPanel.INITIAL_HEIGHT }}>
                    {coreMap}
                </Flex>
            );
        }
    }

    renderRrtVpd(flights: FlightDto[], mapOptions: FlightMapOptions) {
        const vpdMapOptions = Utils.isExists(mapOptions) ? mapOptions : DEFAULT_MAP_OPTIONS;

        return (
            <Flex direction={'vertical'}>
                <Flex
                    size={'2m'}
                    direction={'horizontal'}
                    className="centered"
                    style={{ alignItems: 'center', gap: '1em' }}>
                    <ConditionalRender condition={flights.length > 1}>
                        <Select
                            value={this.state.selectedVpdFlight}
                            onChange={this.handleVpdSelectedFlight}
                            listHeight={320}
                            style={{ width: '10em' }}>
                            {flights.map(flight => (
                                <Select.Option
                                    key={flight.flightId.flightKeys.callsign}
                                    value={flight.flightId.flightKeys.callsign}>
                                    {flight.flightId.flightKeys.callsign}
                                </Select.Option>
                            ))}
                        </Select>
                    </ConditionalRender>
                    <Switch
                        style={{ width: '10em' }}
                        unCheckedChildren={'Showing RRT'}
                        checkedChildren={'Showing Original'}
                        checked={this.state.showOri}
                        onChange={this.handleShowOriVpd}
                    />
                </Flex>
                <Flex>
                    {this.renderVpdForRrtFlight(
                        this.getFlightByFlightId(flights, this.state.selectedVpdFlight),
                        [],
                        vpdMapOptions,
                    )}
                </Flex>
            </Flex>
        );
    }

    getFlightByFlightId(flights: FlightDto[], flightId: string): FlightDto {
        return flights.find(flight => flight.flightId?.flightKeys?.callsign === flightId);
    }
    getFlightAndModelByFlightId(flights: FlightDto[], flightId: string): FlightAndModelType {
        const flight = flights.find(flight => flight.flightId?.flightKeys?.callsign === flightId);
        if (flight) {
            return new FlightAndModelType(flight, this.state.showOri ? FlightModelType.FTFM : FlightModelType.RTFM);
        }
        return null;
    }

    handleVpdSelectedFlight = flightId => {
        this.setState({ selectedVpdFlight: flightId });
    };

    renderVpdForRrtFlight = (
        flight: FlightDto,
        highlightedSegments: ProfileSegmentHighlight[],
        flightMapOptions: FlightMapOptions,
    ) => {
        //get the crossings
        // let vpdCrossings: VpdFlightDataDto = flight && this.getVpdCrossings(FlightUtils.getEnrichedFlightId(flight))

        return (
            <VerticalProfileSvg
                flight={flight}
                modelType={this.state.showOri ? FlightModelType.FTFM : FlightModelType.RTFM}
                vpdCrossings={null}
                showIfpuzOnly={flightMapOptions.showIfpzPartOfTrajectoryOnly}
                highlightedPoints={null}
                highlightedSegments={highlightedSegments}
                pointSelectionHandler={pt => {}}
                showDynamicVolumeCrossings={flightMapOptions.showActiveDynamicVolumeInVpd}
                airspacesOfInterest={[]}
            />
        );
    };

    handleShowOriVpd = (checked: boolean) => {
        this.setState({
            showOri: checked,
        });
    };

    handleOnSplitSize = () => {
        // this.coreMapRef.current?.forceMapRender();
    };

    handleOnCoreMapIdGeneration = (mapId: number) => {
        this.setState({ mapId: mapId });
    };

    handleMapTimeChange = (value: Date) => {
        this.setState({
            mapTime: value,
        });
    };

    handleMapFlightLevelBandChange = (value: FlightLevelBand) => {
        this.setState({ flightLevelFilter: value });
    };

    handleMapResetMapRefTime = () => {
        this.setState({
            mapTime: Utils.isNotExists(this.props.timePeriod)
                ? AppContext.getCurrentTime()
                : TimePeriodUtils.contains(this.props.timePeriod, AppContext.getCurrentTime())
                  ? AppContext.getCurrentTime()
                  : this.props.timePeriod.from,
        });
    };
}
